// Minimal client for the signed-in-user Cognito operations MedoPilot Account & Security needs.
//
// These operations authenticate with the user's own access token (never a client secret, never a
// MedoPilot backend). Cognito stays authoritative for passwords and MFA -- this module only
// forwards the user's intent and never stores a password or a TOTP secret. It speaks the Cognito
// Identity Provider JSON API directly (AWS JSON 1.1) so the app takes no AWS SDK dependency; the
// access-token operations here require no request signing.

export class CognitoOperationError extends Error {
  readonly code: string;

  constructor(message: string, code: string) {
    super(message);
    this.name = "CognitoOperationError";
    this.code = code;
  }
}

export type CognitoMfaState = {
  // Whether software-token (authenticator app / TOTP) MFA is currently active for the user.
  softwareTokenMfaEnabled: boolean;
  // The user's preferred MFA method as Cognito reports it, or null when none is preferred.
  preferredMfa: string | null;
};

function endpointFor(region: string): string {
  return `https://cognito-idp.${region}.amazonaws.com/`;
}

function parseJson(text: string): Record<string, unknown> {
  try {
    const value = JSON.parse(text) as unknown;
    return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function shortErrorCode(body: Record<string, unknown>): string {
  const raw = typeof body.__type === "string" ? body.__type : "";
  const withoutNamespace = raw.includes("#") ? raw.slice(raw.lastIndexOf("#") + 1) : raw;
  return withoutNamespace || "UnknownError";
}

// Deliberately does not surface raw Cognito exception text to ordinary users. The one place a
// server message genuinely helps -- an invalid new password -- is handled by the page, which
// always shows the fixed policy (Cognito remains authoritative for it).
function friendlyMessage(operation: string, code: string, rawMessage: string): string {
  // A token issued before the self-service scope was granted (e.g. during rollout) fails these
  // calls with NotAuthorizedException + a scope message -- that is a stale session, not a wrong
  // password.
  if (code === "NotAuthorizedException" && /scope/i.test(rawMessage)) {
    return "Please sign out and sign in again to manage your account security.";
  }
  switch (code) {
    case "NotAuthorizedException":
      return operation === "ChangePassword"
        ? "Your current password is incorrect."
        : "Your session has expired. Please sign in again.";
    case "InvalidPasswordException":
    case "InvalidParameterException":
      return operation === "ChangePassword"
        ? "That new password does not meet the requirements below."
        : "Please check the details and try again.";
    case "LimitExceededException":
    case "TooManyRequestsException":
      return "Too many attempts. Please wait a little while and try again.";
    case "CodeMismatchException":
    case "EnableSoftwareTokenMFAException":
      return "That code did not match. Enter the current 6-digit code from your authenticator app.";
    case "ExpiredCodeException":
      return "That code has expired. Enter the current code from your authenticator app.";
    case "PasswordResetRequiredException":
    case "UserNotConfirmedException":
    case "UserNotFoundException":
      return "Your session has expired. Please sign in again.";
    case "NetworkError":
      return "We could not reach the sign-in service. Check your connection and try again.";
    case "NotConfigured":
      return "Account security is not available right now.";
    default:
      return "Something went wrong. Please try again.";
  }
}

async function call<TResult>(
  region: string,
  operation: string,
  payload: Record<string, unknown>,
): Promise<TResult> {
  let response: Response;
  try {
    response = await fetch(endpointFor(region), {
      method: "POST",
      headers: {
        "Content-Type": "application/x-amz-json-1.1",
        "X-Amz-Target": `AWSCognitoIdentityProviderService.${operation}`,
      },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new CognitoOperationError(friendlyMessage(operation, "NetworkError", ""), "NetworkError");
  }

  const text = await response.text();
  const body = text ? parseJson(text) : {};

  if (!response.ok) {
    const code = shortErrorCode(body);
    const rawMessage =
      typeof body.message === "string" ? body.message : typeof body.Message === "string" ? body.Message : "";
    throw new CognitoOperationError(friendlyMessage(operation, code, rawMessage), code);
  }

  return body as TResult;
}

function requireRegion(region: string | undefined): string {
  if (!region) {
    throw new CognitoOperationError("Account security is not available right now.", "NotConfigured");
  }
  return region;
}

export async function getMfaState(region: string | undefined, accessToken: string): Promise<CognitoMfaState> {
  const result = await call<{ UserMFASettingList?: string[]; PreferredMfaSetting?: string }>(
    requireRegion(region),
    "GetUser",
    { AccessToken: accessToken },
  );
  const list = Array.isArray(result.UserMFASettingList) ? result.UserMFASettingList : [];
  return {
    softwareTokenMfaEnabled: list.includes("SOFTWARE_TOKEN_MFA"),
    preferredMfa: typeof result.PreferredMfaSetting === "string" ? result.PreferredMfaSetting : null,
  };
}

export async function changePassword(
  region: string | undefined,
  accessToken: string,
  previousPassword: string,
  proposedPassword: string,
): Promise<void> {
  await call(requireRegion(region), "ChangePassword", {
    AccessToken: accessToken,
    PreviousPassword: previousPassword,
    ProposedPassword: proposedPassword,
  });
}

// Returns the base32 secret Cognito generated for this enrolment attempt. The caller shows it (and
// an otpauth URI built from it) to the user and never persists it.
export async function associateSoftwareToken(region: string | undefined, accessToken: string): Promise<string> {
  const result = await call<{ SecretCode?: string }>(requireRegion(region), "AssociateSoftwareToken", {
    AccessToken: accessToken,
  });
  if (!result.SecretCode) {
    throw new CognitoOperationError(friendlyMessage("AssociateSoftwareToken", "UnknownError", ""), "UnknownError");
  }
  return result.SecretCode;
}

export async function verifySoftwareToken(
  region: string | undefined,
  accessToken: string,
  userCode: string,
  friendlyDeviceName?: string,
): Promise<void> {
  const result = await call<{ Status?: string }>(requireRegion(region), "VerifySoftwareToken", {
    AccessToken: accessToken,
    UserCode: userCode,
    ...(friendlyDeviceName ? { FriendlyDeviceName: friendlyDeviceName } : {}),
  });
  if (result.Status !== "SUCCESS") {
    throw new CognitoOperationError(friendlyMessage("VerifySoftwareToken", "CodeMismatchException", ""), "CodeMismatchException");
  }
}

export async function setSoftwareTokenMfaEnabled(
  region: string | undefined,
  accessToken: string,
  enabled: boolean,
): Promise<void> {
  await call(requireRegion(region), "SetUserMFAPreference", {
    AccessToken: accessToken,
    SoftwareTokenMfaSettings: { Enabled: enabled, PreferredMfa: enabled },
  });
}

// otpauth:// provisioning URI (RFC / Key Uri Format). Authenticator apps consume this directly;
// on mobile it can be opened as a link. Only ever built in the browser from the just-issued
// secret -- never logged or sent anywhere.
export function buildTotpUri(options: { issuer: string; accountName: string; secret: string }): string {
  const label = `${encodeURIComponent(options.issuer)}:${encodeURIComponent(options.accountName)}`;
  const params = new URLSearchParams({
    secret: options.secret,
    issuer: options.issuer,
    algorithm: "SHA1",
    digits: "6",
    period: "30",
  });
  return `otpauth://totp/${label}?${params.toString()}`;
}

// Groups the base32 secret into 4-character blocks for legible manual entry.
export function formatSecretForDisplay(secret: string): string {
  return (secret.match(/.{1,4}/g) ?? [secret]).join(" ");
}
